import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { GbGemeindeListEntry, GbgemeindeService, GbgrundbuchamtService, GrundbuchamtDetailEntry } from '../../../core/gb-api';
import { PolitischeGemeindeListEntry, StammdatengemeindeService } from '../../../core/stammdaten-api';
import { Observable, of, Subscription, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { AvgeometerbueroService, GeometerBueroListEntry, GeometerBueroSearchResponse } from '../../../core/av-api';
import { Clipboard } from 'ts-clipboard';
import { AnschriftAdress, FormatAnschriftAdressPipe } from '../../../shared/pipes/format/format-anschrift-address.pipe';
import { CapAlertService } from '../../../core/extension/exceptions/cap-alert.service';
import { TranslateNamePipe } from '../../../shared/pipes/simple/translate-name.pipe';
import { ErweiterungConfigResponse } from '../../../core/base-api';
import { FormatConfigService } from '../../../core/services/format-config.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { keysToTranslate } from 'i18n/keys-to-translate';
import { environment } from '../../../../environments/environment';
import { ContentHeaderComponent } from '../../../shared/components/content-header/content-header.component';
import { TitleComponent } from '../../../shared/components/title/title.component';
import { RubrikComponent } from '../../../shared/components/rubrik/rubrik.component';
import { KopfdatenelementComponent } from '../../../shared/widgets/kopfdatenelement/kopfdatenelement.component';
import { MapsComponent } from '../../../shared/widgets/maps/maps.component';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { AsyncPipe } from '@angular/common';
import { YesNoPipe } from '../../../shared/pipes/simple/yes-no.pipe';
import ActiveErweiterungenEnum = ErweiterungConfigResponse.ActiveErweiterungenEnum;

@Component({
  selector: 'cap-gb-gemeinde-detail',
  templateUrl: './gb-gemeinde-detail.component.html',
  styleUrls: ['./gb-gemeinde-detail.component.scss'],
  imports: [
    ContentHeaderComponent,
    TitleComponent,
    RubrikComponent,
    KopfdatenelementComponent,
    MapsComponent,
    NgbTooltip,
    AsyncPipe,
    TranslatePipe,
    TranslateNamePipe,
    YesNoPipe
  ]
})
export class GbGemeindeDetailComponent implements OnInit, OnDestroy {
  readonly ID_PARAMETER_NAME = 'id';
  id: string;

  hasPermissionPlan;
  planAvailableForGemeindeType: boolean;
  planGemeindeModel;

  gbGemeinde$: Observable<GbGemeindeListEntry>;
  polGemeinde$: Observable<PolitischeGemeindeListEntry>;
  grundbuchAmt$: Observable<GrundbuchamtDetailEntry>;
  geometerBuero$: Observable<GeometerBueroListEntry>;
  isPublic: boolean;
  private subscription: Subscription = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private gemeindeService: GbgemeindeService,
    private geometerBueroService: AvgeometerbueroService,
    private polGemeindeService: StammdatengemeindeService,
    private grundbuchamtService: GbgrundbuchamtService,
    private formatAnschriftAdresse: FormatAnschriftAdressPipe,
    private capAlertService: CapAlertService,
    public translateNamePipe: TranslateNamePipe,
    protected appFormatConfigService: FormatConfigService,
    private translate: TranslateService
  ) {
    keysToTranslate<string>([
      'gehoertZuFolgenderPolitischerGemeinde',
      'zustaendigesGrundbuchamt',
      'zustaendigerNachfuehrungsgeometer',
      'kreisNummer',
      'kuerzel',
      'gueltig',
      'bfsNummer',
      'externeGemeinde',
      'kanton',
      'anschrift',
      'email',
      'telefonnummer1',
      'telefax',
      'mehrereNachfuehrungsgeometer'
    ]);
    this.route.params.subscribe((params) => {
      this.id = params[this.ID_PARAMETER_NAME];
    });
    this.isPublic = environment.deploymentMode === 'PUBLIC';
  }

  public notEmpty = (val: any) => val;

  ngOnInit() {
    this.subscription.add(
      this.appFormatConfigService.erweiterungsConfig$.subscribe((config) => {
        this.hasPermissionPlan = config?.activeErweiterungen.includes(ActiveErweiterungenEnum.ANZEIGE_PLAN_GEM_KREIS);
      })
    );

    this.gbGemeinde$ = this.gemeindeService.loadGbGemeindeListEntryById(this.id).pipe(
      tap({
        next: (entry) => {
          this.planGemeindeModel = entry;
          this.planAvailableForGemeindeType = this.isPlanAllowed(entry);
          if (!entry.politischeGemeindeId) {
            return;
          }
          this.polGemeinde$ = this.polGemeindeService.loadGemeinde(entry.politischeGemeindeId.id).pipe(
            tap({
              next: (polGemeinde) => (this.planAvailableForGemeindeType = this.planAvailableForGemeindeType && !polGemeinde.extern),
              error: this.filterEntityNotFoundErrors()
            })
          );
          this.grundbuchAmt$ = this.grundbuchamtService
            .loadGrundbuchamtDetailEntry(entry.politischeGemeindeId.id)
            .pipe(this.filterEntityNotFoundErrors()) as Observable<GrundbuchamtDetailEntry>;
          this.geometerBuero$ = this.geometerBueroService
            .loadGeometerBueros(entry.politischeGemeindeId.id)
            .pipe(map((b) => this.findBueroWithGeometer(b)))
            .pipe(this.filterEntityNotFoundErrors());
        },
        error: this.filterEntityNotFoundErrors()
      })
    );
  }

  convertGrundbuchamtToAdress(detailEntry: GrundbuchamtDetailEntry) {
    return <AnschriftAdress>{
      name: this.translateNamePipe.transform(detailEntry.name),
      strasse: detailEntry.strasse,
      plz: detailEntry.plz,
      ort: this.translateNamePipe.transform(detailEntry.ort)
    };
  }

  convertGeometerbueroToAdress(detailEntry: GeometerBueroListEntry) {
    return <AnschriftAdress>{
      name: this.translateNamePipe.transform(detailEntry.name),
      person: [detailEntry.nachfuehrungsGeometer.userName, detailEntry.nachfuehrungsGeometer.userVorname].filter(this.notEmpty).join(' '),
      strasse: detailEntry.strasse,
      plz: detailEntry.plz,
      ort: this.translateNamePipe.transform(detailEntry.ort)
    };
  }

  isPlanAllowed(gemeinde: GbGemeindeListEntry): boolean {
    return !(
      !gemeinde.politischeGemeindeId ||
      gemeinde.gemeindeArt === GbGemeindeListEntry.GemeindeArtEnum.SEYBUCH ||
      gemeinde.gemeindeArt === GbGemeindeListEntry.GemeindeArtEnum.AUSLAENDISCHE_GEMEINDE ||
      !gemeinde.gueltig
    );
  }

  copyToClipboard(adress: AnschriftAdress) {
    const address = this.formatAnschriftAdresse.transform(adress);
    Clipboard.copy(address);
    this.capAlertService.info(this.translate.instant('AdresseWurdeKopiert'));
  }

  private filterEntityNotFoundErrors() {
    return catchError((e) => {
      if (e.status === 404) {
        return of();
      }
      return throwError(e);
    });
  }

  private findBueroWithGeometer(response: GeometerBueroSearchResponse) {
    const bueroWithGeometer = response.entries.filter((entry) => entry.nachfuehrungsGeometer);
    if (!bueroWithGeometer) {
      return null;
    }
    if (bueroWithGeometer.length > 1) {
      this.capAlertService.warning(this.translate.instant('mehrereNachfuehrungsgeometer'));
    }
    return bueroWithGeometer[0];
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
